import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { calculateTimeInSeconds } from '../../utils';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  generalInfoSummaryQuery,
  consolidatedScoresSummaryQuery,
  scoresStatsQuery
} from './api';

import { popupMessagesSummary } from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';
import Summary from '../UI/game/summary';
import PerformanceRankingInfo from '../UI/performanceRankingInfo';

import './styles.css';

export default function NormalBalanceSummary() {
  const navigate = useNavigate();

  const {
    getUser,
    getCurrentNormalGameId
  } = useAuth();

  const user = getUser();

  const gameId = getCurrentNormalGameId();

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);
  const [scenarioId, setScenarioId] = useState(0);
  const [rank, setRank] = useState('');

  const [points, setPoints] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [averageTime, setAverageTime] = useState(0);

  const [firstAttemptPoints, setFirstAttemptPoints] = useState(0);
  const [firstAttemptAccuracy, setFirstAttemptAccuracy] = useState(0);
  const [firstAttemptAverageTime, setFirstAttemptAverageTime] = useState(0);

  const [isPerformanceRankingInfoVisible, setIsPerformanceRankingInfoVisible] = useState(false);

  const [dataLoaded, setDataLoaded] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const { data: consolidatedScores } = useQuery(consolidatedScoresSummaryQuery());

  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId, attempt: currentGameSessionAttempt }));

  const normalScores = useMemo(() => consolidatedScores?.normal_balance || [], [consolidatedScores]);

  const currentSessionScores = useMemo(() => {
    if (!gamesSessions || !normalScores.length) return null;

    const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
    const currentGameSession = currentGameSessions.find(session => !session.completed)
      || currentGameSessions.filter(session => session.completed).reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null);

    return normalScores.find(s => s.game === gameId && s.game_session === currentGameSession?.id);
  }, [gamesSessions, normalScores, gameId, user?.id]);

  const firstAttemptSessionScores = useMemo(() => {
    return normalScores.find(s => s.game === gameId && s.attempt === 1);
  }, [normalScores, gameId]);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);

      if (currentGameSession) {
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = currentGameSessions
          .filter(session => session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);

        setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Normal Balance') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  useEffect(() => {
    if(currentSessionScores && scoresStats) {
      const summary = currentSessionScores.session_summary;
      const totalPoints = summary.total_points_session;
      const totalAccuracy = summary.total_accuracy_session;

      const levels = currentSessionScores.levels || [];
      const avgTime = levels.length > 0
        ? (levels.reduce((sum, l) => sum + calculateTimeInSeconds(l.average_time), 0) / levels.length).toFixed(2)
        : 0;

      setPoints(totalPoints);
      setAccuracy(totalAccuracy);
      setAverageTime(avgTime);

      // Rank is computed server-side and exposed as session.rank_session,
      // matching what normal-balance/badge displays. Reading it from the
      // same source keeps the two pages in sync.
      setRank(scoresStats.session?.rank_session || '');
      setDataLoaded(true);
    }
  }, [currentSessionScores, scoresStats]);

  useEffect(() => {
    if(firstAttemptSessionScores) {
      const summary = firstAttemptSessionScores.session_summary;
      setFirstAttemptPoints(summary.total_points_session);
      setFirstAttemptAccuracy(summary.total_accuracy_session);

      const levels = firstAttemptSessionScores.levels || [];
      const avgTime = levels.length > 0
        ? (levels.reduce((sum, l) => sum + calculateTimeInSeconds(l.average_time), 0) / levels.length).toFixed(2)
        : 0;
      setFirstAttemptAverageTime(avgTime);
    }
  }, [firstAttemptSessionScores]);

  const handlePopupMessagesNextButtonClick = () => {
    navigate('/normal/summary/breakdown');
  }

  const popupMessagesFormatted = useMemo(() => {
    let formattedMessages = currentGameSessionAttempt > 1
      ? popupMessagesSummary
      : [
          {
            ...popupMessagesSummary[0],
            text: 'Congratulations on completing the exercise!<br><br>' + popupMessagesSummary[0].text
          },
          ...popupMessagesSummary.slice(1)
        ];

    if(formattedMessages) {
      formattedMessages = formattedMessages.map(message => ({
        ...message,
        text: message.text.replace('{accuracy}', `${(accuracy * 100).toFixed(2)}%`)
          .replace('{averageTime}', `${averageTime} seconds`)
          .replace('{points}', points.toLocaleString('en-US'))
          .replace('{rank}', rank)
      }));
    }

    return formattedMessages;
  }, [currentGameSessionAttempt, accuracy, averageTime, points, rank]);

  const handleBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(true);
  }

  const handleCloseBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(false);
  }

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        isPerformanceRankingInfoVisible &&
          <div className="background-overlay">
            <PerformanceRankingInfo position="center" onCloseButtonClick={handleCloseBadgeInfoButtonClick} />
          </div>
      }
      {
        dataLoaded &&
          <div className="game-summary-container">
            <PopupMessages
              messages={popupMessagesFormatted}
              buttonText="Next"
              messageSize="small"
              onButtonClick={handlePopupMessagesNextButtonClick}
            />
            <Summary
              rank={rank}
              points={points}
              firstAttemptPoints={firstAttemptPoints}
              lines={[
                {label: 'Accuracy', value: accuracy, firstAttemptValue: firstAttemptAccuracy},
                {label: 'Average Time', value: averageTime, firstAttemptValue: firstAttemptAverageTime},
              ]}
              onBadgeInfoButtonClick={handleBadgeInfoButtonClick}
            />
          </div>
      }
    </Layout>
  );
}
